import * as React from 'react';
import { ReactComponent as Check } from '../assets/check.svg';
import { Card } from '../components/Card';
import { Input } from '../components/Input';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormSchemaType, stepOneSchema, stepTwoSchema, StepTwoType } from '../schemas/formSchema';
import { useInputFields } from '../contexts/InputFields';
import { useNavigate, useLocation } from 'react-router-dom';
import { format } from '@react-input/mask';
import { Select, SelectOption } from "../components/Select";
import { usePlans } from "../contexts/Plans";
import { normalizeDocument, toBrl } from "../services/functions";
import Dropdown from "../components/Dropdown";
import { DiscountTicket } from "../components/DiscountTicket";
import { Button } from "../components/Button";
import { useEffect, useState } from "react";
import { CompanyApi } from "../api/company";
import Swal from "sweetalert2";
import { TurnstileWidget, TurnstileWidgetRef } from "../components/TurnstileWidget";

const paymentLabels: Record<string, string> = {
    credit_card: 'Cartão de crédito',
    pix: 'PIX',
}

export const StepTwo = () => {
    const { formData, onSubmit, onError } = useInputFields();
    const captchaRef = React.useRef<TurnstileWidgetRef>(null);
    const [loading, setLoading] = useState(false);
    const [captchaLoading, setCaptchaLoading] = useState(true);
    const [captchaError, setCaptchaError] = useState<string | null>(null);
    const navigate = useNavigate();
    const location = useLocation();

    const { createCompany, reenviarCodigoSms } = CompanyApi();
    const [cooldown, setCooldown] = useState(60);
    const [reenviando, setReenviando] = useState(false);
    const {
        plans,
        selectedPlan,
        setSelectedPlan,
        selectedInstallment,
        setSelectedInstallment,
        paymentMethod,
        setPaymentMethod,
        discountTicket,
        installmentsOptions,
        allowPix,
        reativa
    } = usePlans();

    const optionsPlans: SelectOption[] = plans.map(plan => ({
        value: plan.id_superlogica.toString(),
        label: `Plano ${plan.periodicidade} | ${plan.nome}`
    }));

    const {
        register,
        watch,
        setValue,
        handleSubmit,
        formState: { errors }
    } = useForm<StepTwoType>({
        resolver: zodResolver(stepTwoSchema),
        defaultValues: {
            ...formData,
            paymentMethod,
            politicas: true,
        }
    });

    useEffect(() => {
        // update payment method in form data to validate if need cards or not
        setValue("paymentMethod", paymentMethod, { shouldValidate: true });
    }, [paymentMethod, setValue]);

    useEffect(() => {
        setValue("installments", selectedInstallment!, { shouldValidate: true });
    }, [selectedInstallment, setValue]);

    // if formData isn't valid in step 1 → back to step 1 preserving URL parameters
    useEffect(() => {
        const result = stepOneSchema.safeParse(formData);
        if (!result.success) {
            navigate({
                pathname: '/',
                search: location.search
            }, { replace: true });
        }
    }, []);

    // Cooldown do botão "Reenviar código" (sincronizado com o cooldown de 60s do backend)
    useEffect(() => {
        if (cooldown <= 0) return;
        const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
        return () => clearInterval(t);
    }, [cooldown]);

    const handleReenviarCodigo = async () => {
        if (cooldown > 0 || reenviando) return;
        setReenviando(true);
        try {
            await reenviarCodigoSms({
                nome: formData.fullName ?? '',
                telefone: formData.phone ?? '',
            });
            setCooldown(60);
            Swal.fire({
                icon: 'success',
                title: 'Código reenviado',
                text: 'Verifique seu SMS.',
                confirmButtonText: 'OK'
            });
        } catch (error: any) {
            const msg = error?.response?.data?.erro || 'Não foi possível reenviar o código. Tente novamente.';
            Swal.fire({
                icon: 'error',
                title: 'Erro ao reenviar código',
                text: msg,
                confirmButtonText: 'OK'
            });
        } finally {
            setReenviando(false);
        }
    };

    // Watch raw digits
    const rawCpfCnpj = watch('cpfCnpj') || '';
    const digits = normalizeDocument(rawCpfCnpj);
    const isCnpj = digits.length > 11;
    // CNPJ alfanumérico: 12 primeiras posições aceitam letra ou dígito (A), DVs apenas dígito (D)
    const mask = isCnpj ? 'AA.AAA.AAA/AAAA-DD' : 'DDD.DDD.DDD-DD';
    const formatted = format(digits, { mask, replacement: { 'D': /\d/, 'A': /[0-9A-Za-z]/ } });

    // Verificando se o plano selecionado é anual
    const isAnualPlan = selectedPlan?.periodicidade?.toLowerCase().includes('anual');

    const handleFormSubmit = async (data: StepTwoType) => {
        if (!selectedPlan) {
            Swal.fire({
                icon: 'error',
                title: 'Erro!',
                text: 'Selecione um plano para continuar.',
                confirmButtonText: 'OK'
            })
            return;
        }
        onSubmit(data);

        setLoading(true);
        try {
            const res = await createCompany(formData as FormSchemaType, selectedPlan, discountTicket);
            if (res !== true && !res.link_boleto == undefined) {
                Swal.fire({
                    icon: 'error',
                    title: 'Erro inesperado',
                    text: 'Aguarde um momento e tente novamente!',
                    confirmButtonText: 'OK'
                })
                return;
            }

            // Preserve URL parameters when navigating to success page
            navigate({
                pathname: '/sucesso'
            });
        } catch (error: any) {
            if (error.status === 409) {
                Swal.fire({
                    icon: 'error',
                    title: 'E-mail já cadastrado!',
                    text: 'Esse e-mail já está cadastrado, tente outro.',
                    confirmButtonText: 'OK'
                })
                setLoading(false)
                return;
            }
            if (error.status === 422) {
                const backendMsg = error?.response?.data?.erro;
                Swal.fire({
                    icon: 'error',
                    title: 'Não foi possível continuar',
                    text: backendMsg || ('Ocorreu um erro com o cartão informado: ' + (error.message || 'Dados inválidos.')),
                    confirmButtonText: 'OK'
                })
                setLoading(false)
                return;
            }
            if (error.status === 429) {
                Swal.fire({
                    icon: 'error',
                    title: 'Erro de captcha!',
                    text: 'Aguarde um momente e tente novamente!',
                    confirmButtonText: 'OK'
                })
                setLoading(false)
                return;
            }
            if (error.status === 503) {
                Swal.fire({
                    icon: 'error',
                    title: 'Erro no servidor!',
                    text: 'Estamos com problemas técnicos, tente novamente mais tarde.',
                    confirmButtonText: 'OK'
                })
                return;
            }

            Swal.fire({
                icon: 'error',
                title: 'Erro inesperado',
                text: 'Aguarde um momento e tente novamente!',
                confirmButtonText: 'OK'
            })
            return;
        } finally {
            setLoading(false);
            captchaRef.current?.reset();
        }
    };

    // Helper function to go back to step one with preserved URL parameters
    const handleGoBack = () => {
        navigate({
            pathname: '/',
            search: location.search
        });
    };

    if (!selectedPlan) {
        return (
            <div className="w-full grid grid-cols-[63fr_37fr] gap-6 h-[50vh] lg:h-[100vh] lg:grid-cols-1">
                <div className="skeleton" />
                <div className="skeleton" />
            </div>
        )
    }

    return (
        <form onSubmit={handleSubmit(handleFormSubmit, onError)} noValidate className="w-full" name='step-one-form'>
            <div className="w-full grid grid-cols-[63fr_37fr] gap-6 lg:grid-cols-1">
                <div className="w-full card pb-28">
                    <div className="flex flex-col gap-2">
                        <div className="flex justify-between items-center">
                            <div className="title">Autenticação</div>
                            <div className="text-12 text-tertiary">
                                Você recebeu via SMS um código de autenticação para validar sua indentidade.
                            </div>
                        </div>

                        <Input
                            label="Código de autenticação"
                            placeholder="000000"
                            mask="DDDDDD"
                            replacement={{ 'D': /\d/ }}
                            inputMode="numeric"
                            {...register('codigoSms')}
                        />
                        <button
                            type="button"
                            onClick={handleReenviarCodigo}
                            disabled={cooldown > 0 || reenviando}
                            className="text-[11px] text-primary underline self-end disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
                        >
                            {reenviando
                                ? 'Reenviando...'
                                : cooldown > 0
                                    ? `Reenviar código em ${cooldown}s`
                                    : 'Reenviar código'}
                        </button>
                    </div>
                    <div className="separator-line mt-4 mb-8" />

                    <div className="title">Dados de cadastro</div>
                    <div className="mt-4">
                        <Input
                            {...register('cpfCnpj')}
                            value={formatted}
                            onChange={e => {
                                const onlyDigits = normalizeDocument(e.target.value);
                                setValue('cpfCnpj', onlyDigits, { shouldValidate: true });
                            }}
                            onBlur={() => setValue('cpfCnpj', digits, { shouldValidate: true })}
                            label="CPF / CNPJ"
                            placeholder={isCnpj ? 'AA.AAA.AAA/AAAA-00' : '000.000.000-00'}
                        />
                    </div>

                    <div className="separator-line mt-8 mb-10" />
                    <div className="flex justify-between items-center gap-x-3 flex-wrap">
                        <div className="title text-nowrap">Forma de pagamento</div>
                        {paymentMethod === 'credit_card' && (
                            <div className="text-12 text-tertiary text-nowrap lg:text-wrap">
                                Dê preferência para cartão de credito físico, não virtual
                            </div>
                        )}
                    </div>
                    <div className="mt-4 flex gap-3">
                        <button
                            type="button"
                            onClick={() => setPaymentMethod('credit_card')}
                            className={`flex-1 py-2 px-4 rounded-full border font-bold text-14 ${paymentMethod === 'credit_card'
                                ? 'bg-primary text-white border-primary'
                                : 'text-tertiary border-tertiary'
                                }`}
                        >
                            Cartão de crédito
                        </button>
                        {allowPix && (
                            <button
                                type="button"
                                onClick={() => setPaymentMethod('pix')}
                                className={`flex-1 py-2 px-4 rounded-full border font-bold text-14 ${paymentMethod === 'pix'
                                    ? 'bg-primary text-white border-primary'
                                    : 'text-tertiary border-tertiary'
                                    }`}
                            >
                                PIX
                            </button>
                        )}
                    </div>
                    {paymentMethod === 'credit_card' && (
                        <Dropdown active={true}>
                            <div className="mt-4 flex flex-col gap-2">
                                <Input
                                    label="Número do cartão"
                                    placeholder="0000 0000 0000 0000"
                                    mask="DDDD DDDD DDDD DDDD"
                                    replacement={{ 'D': /\d/ }}
                                    {...register('cardNumber')}
                                    sensitive={true}
                                />
                                <Input
                                    label="Nome impresso no cartão"
                                    placeholder="Nome completo"
                                    {...register('cardHolderName')}
                                    sensitive={true}
                                />
                                {isCnpj && (
                                    <Input
                                        label="CPF"
                                        placeholder="000.000.000-00"
                                        mask="DDD.DDD.DDD-DD"
                                        replacement={{ 'D': /\d/ }}
                                        {...register('cpfResponsavel')}
                                    />
                                )}
                                <div className="grid grid-cols-2 gap-x-4 sm:contents">
                                    <Input
                                        label="Data de validade"
                                        placeholder="00/00"
                                        mask="DD/DD"
                                        replacement={{ 'D': /\d/ }}
                                        {...register('expirationDate')}
                                        sensitive={true}
                                    />
                                    <Input
                                        label="CVV"
                                        placeholder="000"
                                        mask="DDD"
                                        replacement={{ 'D': /\d/ }}
                                        {...register('cvv')}
                                        sensitive={true}
                                    />
                                </div>
                                <Select
                                    label="Parcelas"
                                    placeholder="Seleciona uma opção"
                                    options={installmentsOptions}
                                    value={selectedInstallment?.toString()}
                                    onChange={(e) => {
                                        setSelectedInstallment(Number(e.target.value));
                                    }}
                                />
                            </div>
                        </Dropdown>
                    )}
                    {paymentMethod === 'pix' && (
                        <>
                            {isCnpj && (
                                <div className="mt-4">
                                    <Input
                                        label="CPF do responsável"
                                        placeholder="000.000.000-00"
                                        mask="DDD.DDD.DDD-DD"
                                        replacement={{ 'D': /\d/ }}
                                        {...register('cpfResponsavel')}
                                    />
                                </div>
                            )}
                            <div className="mt-4 p-4 rounded-lg bg-background-green text-14 text-tertiary">
                                Você ganhará <b>14 dias de teste grátis</b>. Somente ao fim do período,
                                será gerado o Pix que ficará disponível em seu painel.
                            </div>
                        </>
                    )}
                </div>
                <Card>
                    <div className="flex gap-6 flex-col leading-4 text-14">
                        <div className="title">Resumo</div>
                        <div className="flex flex-col gap-2.5 max-w-80">
                            <Select
                                placeholder="Selecione um plano"
                                value={selectedPlan?.id_superlogica.toString()}
                                onChange={(e) => {
                                    setSelectedPlan(plans.find(plan => plan.id_superlogica.toString() === e.target.value) || null);
                                }}
                                className={"w-full border border-primary rounded-full py-0 px-8"}
                                options={optionsPlans}
                                renderSelectedValue={(option) => (
                                    <div className="text-14 text-tertiary font-bold">
                                        {option?.label}
                                    </div>
                                )}
                            />
                            {selectedPlan && selectedInstallment && (
                                <>
                                    {selectedPlan.valor_cheio !== selectedPlan.valor && (
                                        <div
                                            className="w-full bg-background-green rounded-full px-8 text-tertiary font-bold relative flex items-center">
                                            <div>
                                                {toBrl(selectedPlan.valor_cheio - selectedPlan.valor)} de desconto
                                                {paymentMethod === 'credit_card' && selectedInstallment > 1
                                                    ? (' em ' + selectedInstallment?.toString() + ' meses')
                                                    : ''}
                                            </div>
                                            <Check className="absolute left-2" />
                                        </div>
                                    )}
                                    <div
                                        className="w-full px-8 text-tertiary flex items-center relative">
                                        {selectedPlan.valor_cheio !== selectedPlan.valor && (
                                            <s>
                                                {toBrl(selectedPlan.valor_cheio / (paymentMethod === 'credit_card' ? selectedInstallment : 1))}/mês&nbsp;
                                            </s>
                                        )}
                                        <b>
                                            {toBrl(selectedPlan.valor / (paymentMethod === 'credit_card' ? selectedInstallment : 1))}/mês
                                        </b>
                                        <Check className="absolute left-2" />
                                    </div>
                                    <div className="px-8 text-tertiary flex items-center font-bold relative">
                                        {paymentMethod === 'credit_card'
                                            ? `${paymentLabels[paymentMethod]} | ${selectedInstallment}x de ${toBrl(selectedPlan.valor / selectedInstallment)}`
                                            : `${paymentLabels[paymentMethod]} | ${toBrl(selectedPlan.valor)} à vista`}
                                        <Check className="absolute left-2" />
                                    </div>
                                    {!reativa && (
                                        <div className="px-8 text-tertiary flex items-center font-bold relative">
                                            Teste grátis por 14 dias
                                            <Check className="absolute left-2" />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    <div className="separator-line my-6" />
                    <DiscountTicket />
                    <div className="separator-line my-6" />
                    {(selectedPlan && selectedInstallment) && (
                        <div className="flex flex-col gap-1 leading-4 text-14">
                            <div className="flex justify-between items-center text-tertiary">
                                <div>Subtotal</div>
                                <div>{toBrl(selectedPlan.valor_cheio / selectedInstallment)}/mês</div>
                            </div>
                            {selectedPlan.valor_cheio !== selectedPlan.valor && (
                                <div className="flex justify-between items-center text-primary">
                                    <div>
                                        Desconto {selectedPlan.periodicidade.toLowerCase()}
                                    </div>
                                    <div>
                                        - {toBrl((selectedPlan.valor_cheio - selectedPlan.valor) / selectedInstallment)}/mês
                                    </div>
                                </div>
                            )}
                            {discountTicket && (
                                <div className="flex justify-between items-center text-primary">
                                    <div>
                                        Desconto cupom
                                    </div>
                                    <div>
                                        - {toBrl(discountTicket.tipo_desconto === 'valor' ? discountTicket.desconto / selectedInstallment : (selectedPlan.valor * (discountTicket.desconto / 100)) / selectedInstallment)}/mês
                                    </div>
                                </div>
                            )}
                            <div className="flex justify-between items-center font-bold text-16 text-tertiary">
                                <div>
                                    Total
                                </div>
                                <div>
                                    {toBrl((selectedPlan.valor - (discountTicket?.tipo_desconto === 'valor' ? discountTicket.desconto : (selectedPlan.valor * (discountTicket?.desconto || 0) / 100))) / selectedInstallment)}/mês
                                </div>
                            </div>
                        </div>
                    )}
                    <br />
                    <TurnstileWidget
                        ref={captchaRef}
                        fieldName="captchaStepTwo"
                        setValue={setValue}
                        register={register}
                        containerId="cf-turnstile-step-two"
                        onLoadingChange={setCaptchaLoading}
                        onErrorChange={setCaptchaError}
                    />
                    <Button className="btn my-6 w-full" type="submit" loading={loading} disabled={captchaLoading || !!captchaError || loading}>
                        {reativa ? 'Contratar agora' : 'Iniciar teste grátis por 14 dias'}
                    </Button>
                    <div className="w-full flex justify-center">
                        <div className="text-tertiary text-12 leading-4">
                            {isAnualPlan ? (
                                <>
                                    Ao continuar, você concorda em iniciar seu teste grátis de 14 dias para um plano anual, com a cobrança automática da assinatura no 15º dia, com a fidelidade de 12 meses, e com nossa {' '}
                                    <a
                                        href="https://assine.conectavenda.com.br/politica-de-privacidade/"
                                        target="_blank"
                                        className="underline"
                                    >
                                        Política de Privacidade
                                    </a>
                                </>
                            ) : (
                                <>
                                    Ao continuar, você concorda em iniciar seu teste grátis de 14 dias, com a cobrança automática da assinatura no 15º dia, e com nossa {' '}
                                    <a
                                        href="https://assine.conectavenda.com.br/politica-de-privacidade/"
                                        target="_blank"
                                        className="underline"
                                    >
                                        Política de Privacidade
                                    </a>
                                </>
                            )}
                            {errors.politicas && (
                                <span className="text-red-500 text-11 mt-1 block">
                                    {errors.politicas.message}
                                </span>
                            )}
                        </div>
                    </div>
                </Card>
            </div>
        </form>
    );
};
